import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Package, User as UserIcon, LayoutDashboard } from "lucide-react";
import api, { formatCAD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field = "w-full rounded-xl border border-border bg-white px-4 py-3 text-sm text-ink outline-none transition-all focus:border-leaf focus:ring-4 focus:ring-leaf/15 placeholder:text-muted-foreground";

function AuthForms() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res =
      mode === "login"
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
    setLoading(false);
    if (!res.ok) { setErr(res.error); return; }
    // Admins go to the dashboard; everyone else returns to where they came from.
    if (res.user?.role === "admin") navigate("/admin");
    else if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="mx-auto max-w-md px-5 py-12">
      <div className="rounded-[2rem] border border-border/70 bg-white p-6 shadow-[0_30px_60px_-35px_rgba(15,74,35,0.35)] sm:p-8">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <img src="/brand/logo-full.png" alt="Bambam African Mart" className="mx-auto h-28 w-auto" />
          <h1 className="mt-4 font-display text-4xl font-semibold text-forest sm:text-5xl">{mode === "login" ? "Welcome back" : "Join the mart"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{mode === "login" ? "Sign in to track your orders." : "Create an account to save your order history."}</p>
        </motion.div>

        <div className="mt-8 flex rounded-full border border-border bg-sand p-1">
          <button data-testid="tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${mode === "login" ? "bg-forest text-white" : "text-forest"}`}>Sign in</button>
          <button data-testid="tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-colors ${mode === "register" ? "bg-forest text-white" : "text-forest"}`}>Register</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name" className={field} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          )}
          <input data-testid="auth-email" type="email" className={field} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          <input data-testid="auth-password" type="password" className={field} placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
          {err && <p data-testid="auth-error" className="text-sm text-destructive">{err}</p>}
          <button data-testid="auth-submit" disabled={loading} className="w-full rounded-full bg-clay py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A3161F] disabled:opacity-60">
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Prefer not to sign in? <Link to="/shop" className="font-semibold text-clay hover:underline">Shop as guest</Link>
        </p>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  pending: "bg-muted text-forest",
  processing: "bg-ochre/20 text-forest",
  shipped: "bg-clay/15 text-clay",
  delivered: "bg-forest/10 text-forest",
  cancelled: "bg-destructive/10 text-destructive",
};

function Dashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/me").then((r) => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="account-dashboard" className="mx-auto max-w-4xl px-5 py-12 lg:px-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-leaf/15 text-forest">
            <UserIcon className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-4xl font-semibold text-forest">Hello, {user.name.split(" ")[0]}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {user.role === "admin" && (
            <Link to="/admin" className="inline-flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 text-sm font-semibold text-sand hover:bg-[#0A3318]">
              <LayoutDashboard className="h-4 w-4" /> Admin
            </Link>
          )}
          <button data-testid="logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-forest hover:bg-forest/5">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>

      <h2 className="mt-12 font-display text-3xl font-semibold text-forest">Order history</h2>
      {loading ? (
        <p className="mt-6 text-muted-foreground">Loading orders…</p>
      ) : orders.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <Package className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-forest">No orders yet</p>
          <Link to="/shop" className="mt-4 inline-block rounded-full bg-clay px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#A3161F]">Start shopping</Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {orders.map((o) => (
            <Link key={o.id} to={`/order/${o.id}`} data-testid={`order-row-${o.order_number}`} className="block rounded-[1.5rem] border border-border/70 bg-white p-5 transition-shadow hover:shadow-md">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-forest">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${STATUS_COLORS[o.status] || "bg-muted"}`}>{o.status}</span>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex -space-x-3">
                  {o.items.slice(0, 4).map((it) => (
                    <img key={it.product_id} src={it.image_url} alt="" className="h-9 w-9 rounded-full border-2 border-card object-cover" />
                  ))}
                </div>
                <span className="font-bold text-forest">{formatCAD(o.total_cents)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Account() {
  const { user } = useAuth();
  if (user === null) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;
  return user ? <Dashboard /> : <AuthForms />;
}
